import { apiClient } from '@/shared/api';
import config from '@/config';
import type {
  User,
  UserInformation,
  UserListResponse,
  UserFilters,
  UserLogin,
  ChangePasswordPayload,
} from '../types';

const BASE_PATH = '/v1/admin/users';

export async function getUsers(filters: UserFilters = {}): Promise<UserListResponse> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await apiClient.get<UserListResponse>(`${BASE_PATH}`, { params });
  return response.data;
}

export async function getUser(userId: string): Promise<User> {
  const response = await apiClient.get(`${BASE_PATH}/${userId}`);
  // Handle nested response structure
  const data = response.data;
  // Direct user object
  if (data?.user_id) return data;
  // Nested in data.user
  if (data?.user?.user_id) return data.user;
  // Nested in data.data
  if (data?.data?.user_id) return data.data;
  // Nested in data.data.user
  if (data?.data?.user?.user_id) return data.data.user;
  return data;
}

export async function updateUser(
  userId: string,
  data: Partial<UserInformation>,
): Promise<User> {
  const response = await apiClient.post<User>(`${BASE_PATH}/${userId}/information`, data);
  return response.data;
}

export async function createUser(data: {
  email: string;
  firstName: string;
  lastName: string;
}): Promise<User> {
  const response = await apiClient.post(`${BASE_PATH}`, data);
  // Handle both nested and flat response: { data: { user } } or { user }
  const respData = response.data;
  return respData.data?.user || respData.user || respData;
}

export async function changeUserPassword(
  userId: string,
  data: ChangePasswordPayload
): Promise<void> {
  // POST for consistency with legacy project (not PUT)
  // API expects { user: { ...fullUserObject }, newPassword: "..." }
  await apiClient.post(`${BASE_PATH}/${userId}/changePassword`, {
    user: data.user,
    newPassword: data.newPassword,
  });
}

export async function sendPasswordResetEmail(userId: string): Promise<void> {
  const loginUrl = config.get<string>('services.login');
  const regUrl = config.get<string>('services.reg');
  const source = (regUrl || '').replace(/^https?:\/\//, '');

  await apiClient.post(`${BASE_PATH}/${userId}/reset`, {
    email_parameters: {
      web_url: loginUrl,
      source: source || 'xelacore.io',
    },
  });
}

export type LoginReminderTemplate =
  | 'licensee_login_reminder'
  | 'licensor_login_reminder'
  | 'agent_login_reminder';

export async function sendLoginReminder(
  email: string,
  template: LoginReminderTemplate
): Promise<void> {
  await apiClient.post('/v1/admin/trigger-notification', {
    emails: [email],
    notification: template,
  });
}

export async function getUserLogins(userId: string): Promise<UserLogin[]> {
  const response = await apiClient.get(`${BASE_PATH}/${userId}/logins`);
  // Handle nested response structure: data.data.logins.data.loginAttempts
  const data = response.data;
  if (Array.isArray(data)) return data;
  // Most common: data.data.logins.data.loginAttempts
  if (data?.data?.logins?.data?.loginAttempts && Array.isArray(data.data.logins.data.loginAttempts)) {
    return data.data.logins.data.loginAttempts;
  }
  // Fallback paths
  if (data?.logins?.data?.loginAttempts && Array.isArray(data.logins.data.loginAttempts)) {
    return data.logins.data.loginAttempts;
  }
  if (data?.data?.loginAttempts && Array.isArray(data.data.loginAttempts)) return data.data.loginAttempts;
  if (data?.loginAttempts && Array.isArray(data.loginAttempts)) return data.loginAttempts;
  return [];
}

export async function linkUserToOrganisation(
  userId: string,
  organisationId: string,
  role?: string
): Promise<void> {
  await apiClient.post(`${BASE_PATH}/${userId}/organisations`, {
    organisation_id: organisationId,
    role,
  });
}

export async function unlinkUserFromOrganisation(
  userId: string,
  organisationId: string
): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${userId}/organisations/${organisationId}`);
}
